import {Component, computed, inject, signal, OnInit, ChangeDetectionStrategy} from "@angular/core"
import {CM_LOGGER_APP} from "@app/common/services/logging"
import {ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms"
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner"
import {MatTooltipModule} from "@angular/material/tooltip"
import {Router, ActivatedRoute} from "@angular/router"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {AuthService} from "@common/services/auth/auth.service"
import {SiloService} from "@common/services/auth/silo.service"
import {CompleteOnboardingGQL, SaveOnboardingProgressGQL} from "@pages/onboarding/onboarding-page.generated"
import {CreateCheckoutSessionGQL, SubscriptionPlansGQL} from "@pages/onboarding/billing.generated"

import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {RadioButtonComponent} from "@app/common/components/inputs/radio-button/radio-button.component"
import {RadioGroupComponent} from "@app/common/components/inputs/radio-group/radio-group.component"

// Key for onboarding state persistence (used when redirecting to/from Stripe)
const ONBOARDING_STATE_KEY = "cm_onboarding_state"

// The Free plan is not a Stripe product: choosing it completes onboarding without a subscription
const FREE_PLAN_ID = "free"
const FREE_PLAN_FEATURES = [
    "PBR Downloads",
    "3D Configurator (up to 5 uploads/month)",
    "Texture Editor (up to 5 uploads/month)",
    "App-Free Augmented Reality (AR)",
    "Easy Website Embedding via iFrame",
]

interface BillingState {
    selectedPlanId: string | null
    billingInterval: "monthly" | "yearly"
}

interface SubscriptionPlan {
    productId: string
    productName: string
    productDescription: string | null
    features: string[]
    monthlyPrice: {
        id: string
        lookupKey: string | null
        currency: string
        unitAmount: number | null
        interval: string
    }
    yearlyPrice: {
        id: string
        lookupKey: string | null
        currency: string
        unitAmount: number | null
        interval: string
    }
    trialPeriodDays: number
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-onboarding-page",
    templateUrl: "onboarding-page.component.html",
    styleUrls: ["onboarding-page.component.scss"],
    imports: [ReactiveFormsModule, MatProgressSpinnerModule, MatTooltipModule, InputContainerComponent, RadioButtonComponent, RadioGroupComponent],
})
export class OnboardingPageComponent implements OnInit {
    private readonly logger = inject(CM_LOGGER_APP)
    private readonly totalSteps = 5
    readonly $showProgressCircle = signal(false)
    readonly $initializing = signal(true)
    readonly $currentStep = signal(1)
    readonly $step2Error = signal<string | null>(null)
    readonly $step3Error = signal<string | null>(null)

    // Plan selection state
    readonly $subscriptionPlans = signal<SubscriptionPlan[]>([])
    readonly freePlanId = FREE_PLAN_ID
    readonly freePlanFeatures = FREE_PLAN_FEATURES
    readonly $selectedPlanId = signal<string>(FREE_PLAN_ID)
    readonly $selectedPaidPlan = computed(() => this.$subscriptionPlans().find((plan) => plan.productId === this.$selectedPlanId()) ?? null)
    readonly $isFreePlanSelected = computed(() => this.$selectedPlanId() === FREE_PLAN_ID)
    readonly $billingInterval = signal<"monthly" | "yearly">("monthly")
    readonly $primaryActionLabel = computed(() => {
        if (this.$isFreePlanSelected()) {
            return "Create Free Account"
        }
        const trialPeriodDays = this.$selectedPaidPlan()?.trialPeriodDays ?? 0
        if (trialPeriodDays > 0) {
            return `Start ${trialPeriodDays}-day free trial`
        }
        return "Continue to payment"
    })
    private pendingPlanId: string | null = null

    readonly $freePlanCurrencySymbol = computed(() => {
        const currency = this.$subscriptionPlans()[0]?.monthlyPrice.currency
        return currency ? this.getCurrencySymbol(currency) : "€"
    })

    readonly $savingsPercentage = computed(() => {
        const plans = this.$subscriptionPlans()
        if (plans.length === 0) return null
        const plan = plans[0]
        const monthlyAnnual = (plan.monthlyPrice.unitAmount || 0) * 12
        const yearly = plan.yearlyPrice.unitAmount || 0
        if (monthlyAnnual === 0) return null
        return Math.round(((monthlyAnnual - yearly) / monthlyAnnual) * 100)
    })

    onboardingForm = new UntypedFormGroup({
        // Step 1: Profile
        firstNameFormControl: new UntypedFormControl("", Validators.required),
        lastNameFormControl: new UntypedFormControl("", Validators.required),
        organizationNameFormControl: new UntypedFormControl(""),
        // Step 3: Experience
        has3dExperienceFormControl: new UntypedFormControl(null, Validators.required),
        // Step 4: Newsletter
        newsletterOptInFormControl: new UntypedFormControl(false),
    })

    readonly notifications = inject(NotificationsService)
    readonly auth = inject(AuthService)
    readonly silo = inject(SiloService)
    readonly completeOnboardingGql = inject(CompleteOnboardingGQL)
    readonly saveOnboardingProgressGql = inject(SaveOnboardingProgressGQL)
    readonly subscriptionPlansGql = inject(SubscriptionPlansGQL)
    readonly createCheckoutSessionGql = inject(CreateCheckoutSessionGQL)
    readonly router = inject(Router)
    readonly route = inject(ActivatedRoute)

    async ngOnInit(): Promise<void> {
        // Check if returning from Stripe with successful checkout
        const sessionId = this.route.snapshot.queryParams["session_id"]
        if (sessionId) {
            // Returning from Stripe - only use DB data as source of truth
            // (skip SSO suggestion which would overwrite the user's saved org name)
            this.prefillFromAuthData()
            this.restoreBillingInterval()
            await this.completeOnboarding()
            return
        }

        // Pre-fill from saved progress in database (for users who navigated away and returned)
        this.prefillFromAuthData()

        // Then try SSO user info (for fresh SSO signups)
        this.prefillFromSsoUserInfo()

        // Check if returning to a specific step (e.g., from Stripe cancel)
        const stepParam = this.route.snapshot.queryParams["step"]
        if (stepParam) {
            const step = parseInt(stepParam, 10)
            if (step >= 1 && step <= this.totalSteps) {
                // Restore billing interval from localStorage
                this.restoreBillingInterval()
                this.$currentStep.set(step)

                // Clear URL params without navigation
                window.history.replaceState({}, "", "/onboarding")
            }
        }

        // Fetch subscription plans
        try {
            const result = await this.subscriptionPlansGql.fetch().toPromise()
            if (result?.data?.subscriptionPlans) {
                const plans = JSON.parse(result.data.subscriptionPlans) as SubscriptionPlan[]
                this.$subscriptionPlans.set(plans)
                // Restore the paid plan chosen before a cancelled Stripe checkout; otherwise keep the Free default
                const restoredPlan = this.pendingPlanId ? plans.find((p) => p.productId === this.pendingPlanId) : undefined
                if (restoredPlan) {
                    this.$selectedPlanId.set(restoredPlan.productId)
                }
                this.pendingPlanId = null
            }
        } catch (error) {
            this.notifications.showError("Failed to load subscription plans")
        } finally {
            this.$initializing.set(false)
        }
    }

    /**
     * Pre-fill form from user data in auth service (saved onboarding progress).
     * This is the primary source of truth for returning users.
     */
    private prefillFromAuthData(): void {
        const user = this.auth.$user()
        if (!user) return

        // Pre-fill personal info
        if (user.firstName) {
            this.onboardingForm.get("firstNameFormControl")?.setValue(user.firstName)
        }
        if (user.lastName) {
            this.onboardingForm.get("lastNameFormControl")?.setValue(user.lastName)
        }

        // Pre-fill organization name if user already has one
        const orgName = user.memberships?.[0]?.organization?.name
        if (orgName) {
            this.onboardingForm.get("organizationNameFormControl")?.setValue(orgName)
        }

        // Pre-fill experience and newsletter from saved progress
        if (user.has3dExperience !== null && user.has3dExperience !== undefined) {
            this.onboardingForm.get("has3dExperienceFormControl")?.setValue(user.has3dExperience)
        }
        if (user.newsletterOptIn) {
            this.onboardingForm.get("newsletterOptInFormControl")?.setValue(user.newsletterOptIn)
        }
    }

    /**
     * Save billing-related state to localStorage before redirecting to Stripe.
     * Only stores plan selection state (not form data, which is in the database).
     */
    private saveBillingState(): void {
        const state = {
            selectedPlanId: this.$selectedPaidPlan()?.productId ?? null,
            billingInterval: this.$billingInterval(),
        }
        localStorage.setItem(ONBOARDING_STATE_KEY, JSON.stringify(state))
    }

    /**
     * Restore billing interval from localStorage (after returning from Stripe).
     */
    private restoreBillingInterval(): void {
        try {
            const stateJson = localStorage.getItem(ONBOARDING_STATE_KEY)
            if (!stateJson) return

            const state = JSON.parse(stateJson) as BillingState

            // Restore billing interval
            if (state.billingInterval) {
                this.$billingInterval.set(state.billingInterval)
            }

            // Store plan ID to restore after plans are loaded
            if (state.selectedPlanId) {
                this.pendingPlanId = state.selectedPlanId
            }
        } catch {
            this.logger.warn("Failed to restore billing state from localStorage")
        }
    }

    /**
     * Clear onboarding state from localStorage (after completing onboarding).
     */
    private clearOnboardingState(): void {
        localStorage.removeItem(ONBOARDING_STATE_KEY)
    }

    /**
     * Save current onboarding progress to the database.
     * Called when transitioning between steps.
     */
    private async saveProgress(): Promise<void> {
        try {
            const firstName = this.onboardingForm.value.firstNameFormControl?.trim() || undefined
            const lastName = this.onboardingForm.value.lastNameFormControl?.trim() || undefined
            const organizationName = this.onboardingForm.value.organizationNameFormControl?.trim() || undefined
            const has3dExperience = this.onboardingForm.value.has3dExperienceFormControl
            const newsletterOptIn = this.onboardingForm.value.newsletterOptInFormControl

            await mutateThrowingErrors(this.saveOnboardingProgressGql)({
                firstName,
                lastName,
                organizationName,
                has3dExperience,
                newsletterOptIn,
            })
        } catch (error) {
            // Silent fail - don't block user navigation for save failures
            this.logger.warn("Failed to save onboarding progress:", error)
        }
    }

    async nextStep(): Promise<void> {
        // Step 2: Validate profile before proceeding
        if (this.$currentStep() === 2) {
            const firstName = this.onboardingForm.get("firstNameFormControl")?.value?.trim()
            const lastName = this.onboardingForm.get("lastNameFormControl")?.value?.trim()
            if (!firstName || !lastName) {
                this.$step2Error.set("Please enter your first and last name")
                return
            }
            this.$step2Error.set(null)
            // Save progress when leaving profile step
            await this.saveProgress()
        }

        // Step 3: Validate experience question before proceeding
        if (this.$currentStep() === 3) {
            const has3dExperience = this.onboardingForm.get("has3dExperienceFormControl")?.value
            if (has3dExperience === null || has3dExperience === undefined) {
                this.$step3Error.set("Please answer the question")
                return
            }
            this.$step3Error.set(null)
            // Save progress when leaving experience step
            await this.saveProgress()
        }

        // Step 4: Save progress when leaving newsletter step
        if (this.$currentStep() === 4) {
            await this.saveProgress()
        }

        // Step 5: Free completes onboarding without a subscription, paid plans go through Stripe
        if (this.$currentStep() === 5) {
            if (this.$isFreePlanSelected()) {
                await this.completeOnboarding()
            } else {
                await this.redirectToStripe()
            }
            return
        }

        if (this.$currentStep() < this.totalSteps) {
            this.$currentStep.set(this.$currentStep() + 1)
        }
    }

    previousStep(): void {
        if (this.$currentStep() > 1) {
            this.$currentStep.set(this.$currentStep() - 1)
        }
    }

    async redirectToStripe(): Promise<void> {
        const selectedPlan = this.$selectedPaidPlan()
        if (!selectedPlan) {
            this.notifications.showError("Please select a plan")
            return
        }

        this.$showProgressCircle.set(true)

        try {
            const price = this.$billingInterval() === "monthly" ? selectedPlan.monthlyPrice : selectedPlan.yearlyPrice
            const priceLookupKey = price.lookupKey

            if (!priceLookupKey) {
                throw new Error("Price lookup key not found")
            }

            // Save billing state to localStorage before redirecting (only plan selection, form data is in DB)
            this.saveBillingState()

            const baseUrl = window.location.origin
            const cancelUrl = `${baseUrl}/onboarding?step=5`

            const result = await mutateThrowingErrors(this.createCheckoutSessionGql)({
                priceLookupKey,
                successUrl: `${baseUrl}/onboarding?session_id={CHECKOUT_SESSION_ID}`,
                cancelUrl,
            })

            if (result?.createCheckoutSession) {
                // Redirect to Stripe Checkout
                window.location.href = result.createCheckoutSession
            }
        } catch (error) {
            this.notifications.showError(error instanceof Error ? error.message : "Failed to create checkout session")
            this.$showProgressCircle.set(false)
        }
    }

    /**
     * Pre-fill the profile form with user data from the auth service.
     * This includes firstName/lastName from the user record and a suggested
     * organization name calculated from the email domain.
     */
    private prefillFromSsoUserInfo(): void {
        const user = this.auth.$user()
        if (!user) return

        // Pre-fill firstName
        if (user.firstName) {
            this.onboardingForm.controls["firstNameFormControl"].setValue(user.firstName)
        }

        // Pre-fill lastName
        if (user.lastName) {
            this.onboardingForm.controls["lastNameFormControl"].setValue(user.lastName)
        }

        // Calculate and pre-fill suggested organization name from email domain,
        // but only if no org name was already set (e.g., from saved progress)
        if (user.email) {
            const currentOrgName = this.onboardingForm.get("organizationNameFormControl")?.value?.trim()
            if (!currentOrgName) {
                const suggestedOrgName = this.calculateSuggestedOrgName(user.email)
                if (suggestedOrgName) {
                    this.onboardingForm.controls["organizationNameFormControl"].setValue(suggestedOrgName)
                }
            }
        }
    }

    /**
     * Calculate a suggested organization name from an email domain.
     * Excludes common personal email providers (gmail, outlook, etc.)
     */
    private calculateSuggestedOrgName(email: string): string | undefined {
        const emailDomain = email.split("@")[1]?.toLowerCase()
        if (!emailDomain) return undefined

        // Skip common personal email providers
        const personalProviders = [
            "gmail.com",
            "googlemail.com",
            "outlook.com",
            "hotmail.com",
            "live.com",
            "yahoo.com",
            "icloud.com",
            "me.com",
            "protonmail.com",
            "proton.me",
        ]
        if (personalProviders.includes(emailDomain)) return undefined

        // Use domain name as org suggestion (capitalize first letter)
        const domainParts = emailDomain.split(".")
        return domainParts[0].charAt(0).toUpperCase() + domainParts[0].slice(1)
    }

    getCurrencySymbol(currency: string): string {
        const symbols: Record<string, string> = {
            eur: "€",
            usd: "$",
            gbp: "£",
        }
        return symbols[currency.toLowerCase()] ?? currency.toUpperCase()
    }

    async completeOnboarding(): Promise<void> {
        this.$showProgressCircle.set(true)

        try {
            const firstName = this.onboardingForm.value.firstNameFormControl?.trim() || undefined
            const lastName = this.onboardingForm.value.lastNameFormControl?.trim() || undefined
            const organizationName = this.onboardingForm.value.organizationNameFormControl?.trim() || undefined
            const has3dExperience = this.onboardingForm.value.has3dExperienceFormControl
            const newsletterOptIn = this.onboardingForm.value.newsletterOptInFormControl

            await mutateThrowingErrors(this.completeOnboardingGql)({
                firstName,
                lastName,
                organizationName,
                has3dExperience,
                newsletterOptIn,
                emailNotifications: false,
            })

            // Clear localStorage state
            this.clearOnboardingState()

            // Refresh user data so route guards see the updated onboardingCompletedAt
            await this.auth.refreshUser()

            // Refresh silo to update organization name in navigation
            await this.silo.load()

            // Navigate to pictures
            const organizationId = this.silo.$silo()?.membership?.organization?.id
            await this.router.navigate(["pictures"], {
                queryParams: {tab: "projects", ...{organizationId}},
                queryParamsHandling: "replace",
            })
        } catch (error) {
            this.notifications.showError(error instanceof Error ? error.message : "Failed to complete onboarding")
        } finally {
            this.$showProgressCircle.set(false)
        }
    }
}
